import React, { useEffect, ChangeEvent, useContext, useRef, useState } from 'react';
import useSetSearchTerm from 'hooks/useSetSearchTerm';
import {
  HomefieldIconArrowLeft,
  HomefieldIconSearch,
  HomefieldIconSignCircledCancelFilled,
} from '@dsg-tech/homefield-react-icons';
import { Divider, Popover } from '@dsg-tech/homefield-react';
import { getFeatureFlagByNameSelector, breakpointSelector } from 'store/selectors';
import {
  displayedSearchTermSelector,
  searchDataSelector,
  searchInputSelector,
  showSaytDrawerSelector,
} from 'store/selectors/saytSelector';
import { StoreIdentifierName } from 'types';
import {
  setDisplayedSearchTerm,
  setFirePreSaytImpression,
  setShowSaytDrawer,
} from 'store/slices/sayt';
import { useAppDispatch, useAppSelector } from 'store/store';
import { getCookie, setCookie } from 'utility/cookie-utils';
import { WindowContext } from 'utility/windowContext';
import { NEW_KEY_CODES } from 'utility/constants';
import { cleanSearchTerm } from 'utility/utility';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import { handleSAYT20CharactersEvent } from 'utility/analytics-events';
import SAYTModalWrapper from './SAYTModalWrapper/SayTModalWrapper';
import SAYTSuggestions from './SAYTSuggestions/SAYTSuggestions';
import SAYTSuggestionsV2 from './SAYTSuggestionsV2/SAYTSuggestionsV2';
import getSearchUtils from './SearchUtils';
import './SearchBar.scss';

type SearchBarProps = {
  headerRef?: React.RefObject<HTMLDivElement>;
  storeIdentifierName: StoreIdentifierName;
  thumbnailApi: string;
  mobileDrawer?: boolean;
};

const SearchBar = ({
  headerRef,
  storeIdentifierName,
  thumbnailApi,
  mobileDrawer,
}: SearchBarProps) => {
  const dispatch = useAppDispatch();
  const win = useContext(WindowContext);
  // checking to see if window object exitst, if it does we grab the search params for use later
  const search = win && window.location ? window.location.search : '';
  // if search params exist then we setup a new URLSearchParam object
  const params = search ? new URLSearchParams(search) : null;
  // utilize the search params object to get our searchTerm that was passed from the url, or set the initial search term to an empty string

  const inputRef = useRef<any>();
  const [showResultsDiv, setShowResultsDiv] = useState(false);
  const sayt20EventFired = useRef<boolean>(false);

  const clearButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const [showClearSearchPopover, setShowClearSearchPopover] = useState(false);
  const [showSearchButtonPopover, setShowSearchButtonPopover] = useState(false);
  const saytRedesignEnabled = useAppSelector(getFeatureFlagByNameSelector('saytRedesignEnabled'));
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  const searchInput = useAppSelector(searchInputSelector);
  const searchData = useAppSelector(searchDataSelector);
  const showSaytDrawer = useAppSelector(showSaytDrawerSelector);
  const displayedSearchTerm = useAppSelector(displayedSearchTermSelector);
  const [setSearchTerm] = useSetSearchTerm();
  const { submitSearch, updateSearchTermSAYT } = getSearchUtils(storeIdentifierName);

  const handleMobileSaytDrawer = () => {
    if (viewPortBreakpoint === 'mobile' && !showSaytDrawer) {
      dispatch(setShowSaytDrawer(true));
    }
  };

  const handleBackButtonMobile = () => {
    dispatch(setShowSaytDrawer(false));
  };

  const SearchInputActivatedEventEmitter = (): void => {
    const searchInputActivatedEvent = new CustomEvent('SearchInputActivated');
    document.dispatchEvent(searchInputActivatedEvent);
    dispatch(setFirePreSaytImpression(false));
  };

  const updateSearchTerm = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length <= 75) {
      dispatch(setDisplayedSearchTerm(event.target.value));
      const cleanedTerm = cleanSearchTerm(event.target.value);
      setSearchTerm(cleanedTerm);

      // SAYT20Characters event and fire once per page view using the ref
      if (event.target.value.length >= 20 && !sayt20EventFired.current) {
        handleSAYT20CharactersEvent();
        sayt20EventFired.current = true;
      }
    }
  };

  const handleClear = (event?: React.KeyboardEvent) => {
    if (
      event?.key === NEW_KEY_CODES.ENTER ||
      event?.key === ' ' ||
      event?.key === NEW_KEY_CODES.ESCAPE ||
      event?.type === 'click'
    ) {
      setSearchTerm('');
      dispatch(setDisplayedSearchTerm(''));
      setShowResultsDiv(false);
      inputRef.current.value = '';
      if (viewPortBreakpoint === 'mobile' && !showSaytDrawer) {
        inputRef?.current?.blur();
      } else {
        inputRef?.current?.focus();
      }
    }
  };

  const searchKeydown = (event: React.KeyboardEvent) => {
    if (event?.key === NEW_KEY_CODES.ENTER && searchInput.length >= 1) {
      const str = cleanSearchTerm(searchInput);
      setCookie('enterSTerm', str);
      setCookie('sTermCookie', str);
      const searchUrl = submitSearch(str, 'enterSearchTerm') ?? '';
      window.location.href = searchUrl;
    }
    if (searchInput?.length <= 2) {
      setShowResultsDiv(false);
    }
  };

  const handleShowNewResultsDiv = () => {
    const hasFewCharacters = searchInput.length > 2 && searchInput.length < 20;
    const hasManyCharacters = searchInput.length >= 20;
    const hasKeywordSuggestions = (searchData?.suggestions?.keywords?.length ?? 0) > 0;
    const hasProductSuggestions = (searchData?.suggestions?.products?.length ?? 0) > 0;

    if (
      (hasFewCharacters && hasKeywordSuggestions) ||
      (hasManyCharacters && (hasKeywordSuggestions || hasProductSuggestions))
    ) {
      setShowResultsDiv(true);
    } else {
      setShowResultsDiv(false);
    }
  };

  const handleInputFocus = () => {
    handleMobileSaytDrawer();
    if (inputRef.current && inputRef.current.value.length > 2) {
      setSearchTerm(inputRef.current.value);
      if (saytRedesignEnabled) {
        handleShowNewResultsDiv();
      } else {
        setShowResultsDiv(true);
      }
    }
  };

  // We don't want to resultsdiv panel to show when we are setting our searchTerm off our params.
  useEffect(() => {
    if (
      (searchData?.suggestions && params && params.get('searchTerm') !== searchInput) ||
      (searchData?.suggestions && !params)
    ) {
      if (saytRedesignEnabled) {
        handleShowNewResultsDiv();
      } else {
        setShowResultsDiv(true);
      }
    }
  }, [searchData]);

  // This function is used to toggle the sticky class on the search bar based on the scroll position. On scroll up, the search bar becomes sticky, and on scroll down, it becomes unsticky.
  // This will run only for search pages for mobile and tablet viewports.
  // We need to use the dsg-header-search-sticky-enabled class as this is used in PLP code to manage the sticky filter button in PLP.
  useEffect(() => {
    if (
      (window.location.href.includes('/f/') ||
        window.location.href.includes('/search/SearchDisplay') ||
        getCookie('stickySearch') === 'true') &&
      (viewPortBreakpoint === 'mobile' || viewPortBreakpoint === 'tablet') &&
      !showSaytDrawer
    ) {
      const searchBar = document.querySelectorAll(
        '.dsg-header-search-sticky-enabled',
      )[0] as HTMLElement;
      const searchBarHeight = searchBar?.clientHeight || 0;
      const stickyGhostDiv = document.querySelector(
        '#header-search-bar-sticky-ghost-div',
      ) as HTMLDivElement;

      let lastScrollTop = 0;
      const searchBarRectTop = searchBar?.getBoundingClientRect().top; // original position of the search bar

      const handleScroll = () => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const isScrollingUp = scrollTop < lastScrollTop;
        if (isScrollingUp) {
          searchBar?.classList.add('sticky');
          if (stickyGhostDiv) {
            stickyGhostDiv.style.height = `${searchBarHeight}px`;
          }
        } else {
          searchBar?.classList.remove('sticky');
          if (stickyGhostDiv) {
            stickyGhostDiv.style.height = '0px';
          }
        }
        // Check if the search bar has reached its original position. In that case, remove the sticky class.
        if (window.scrollY <= searchBarRectTop) {
          searchBar?.classList.remove('sticky');
          if (stickyGhostDiv) {
            stickyGhostDiv.style.height = '0px';
          }
        }
        lastScrollTop = scrollTop <= 0 ? 0 : scrollTop; // For negative scrolling
      };

      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
    return undefined;
  }, [viewPortBreakpoint]);

  useEffect(() => {
    if (mobileDrawer) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (showResultsDiv) {
      if (viewPortBreakpoint === 'mobile') {
        dispatch(setShowSaytDrawer(true));
      } else {
        dispatch(setShowSaytDrawer(false));
      }
    }
  }, [viewPortBreakpoint]);

  return (
    <div
      className={`search-bar-container-new hmf-px-s hmf-px-md-m hmf-pt-s hmf-pt-xlg-0 ${
        mobileDrawer ? 'mobile-drawer' : ''
      }`}>
      <div id="header-search-bar-sticky-anchor" className="hmf-display-block hmf-display-lg-none" />
      <div
        id="header-search-bar-sticky-ghost-div"
        className=" hmf-display-block hmf-display-lg-none"
      />

      <div className="dsg-header-search-sticky-enabled">
        <div
          className={`search-container hmf-display-flex hmf-align-items-center ${
            mobileDrawer ? '' : 'hmf-pl-s'
          }`}
          id="searchBarContainer">
          {mobileDrawer ? (
            <button
              type="button"
              className="back-button hmf-p-xs"
              onClick={() => handleBackButtonMobile()}
              onKeyDown={(e) => handleBackButtonMobile()}
              aria-label="back to search results"
              tabIndex={0}>
              <HomefieldIconArrowLeft width="24px" height="24px" />
            </button>
          ) : (
            <HomefieldIconSearch
              width="20px"
              height="20px"
              className={`search-icon-container-left ${
                searchInput?.length !== 0 ? 'header-search-icon-hidden' : ''
              }`}
            />
          )}
          <input
            type="search"
            enterKeyHint="search"
            id="searchInput"
            className={`search-bar-input hmf-body-l hmf-pr-xxs ${mobileDrawer ? 'hmf-pl-0' : ''}`}
            name="search"
            ref={inputRef}
            autoCapitalize="off"
            autoComplete="off"
            spellCheck="false"
            autoCorrect="off"
            maxLength={75}
            onClick={() => {
              SearchInputActivatedEventEmitter();
              handleInputFocus();
            }}
            onKeyDown={(e) => searchKeydown(e)}
            onChange={(e) => updateSearchTerm(e)}
            onFocus={handleInputFocus}
            placeholder="What are you looking for?"
            aria-label="Search"
            value={displayedSearchTerm}
          />
          <div
            className={`${'clear-search-icon-container-right hmf-display-flex hmf-align-items-center'}`}>
            <button
              className={`clear-button hmf-display-inline-flex hmf-justify-content-center hmf-align-items-center hmf-p-s ${
                searchInput?.length === 0 ? 'hidden' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleClear(e);
              }}
              onKeyDown={(e) => handleClear(e)}
              onFocus={() => setShowClearSearchPopover(true)}
              onBlur={() => setShowClearSearchPopover(false)}
              onMouseEnter={() => setShowClearSearchPopover(true)}
              onMouseLeave={() => setShowClearSearchPopover(false)}
              type="button"
              aria-label="clear search term"
              tabIndex={0}
              ref={clearButtonRef}>
              <HomefieldIconSignCircledCancelFilled height="16px" width="16px" />
            </button>
            {showClearSearchPopover && (
              <Popover
                className="popover-container"
                open={showClearSearchPopover}
                anchorElement={clearButtonRef?.current}
                transparentBackground>
                <div className="label-inverse-base hmf-body-m hmf-p-xs">Clear Search</div>
              </Popover>
            )}
            {searchInput?.length !== 0 && <Divider vertical />}
            <button
              className={`search-button hmf-display-inline-flex hmf-justify-content-center hmf-align-items-center hmf-p-xs ${
                searchInput?.length === 0 ? 'hidden' : ''
              }`}
              onClick={() => {
                if (searchInput?.length !== 0) {
                  window.location.href = submitSearch(searchInput, 'enterSearchTerm') ?? '';
                }
              }}
              onKeyDown={(e) => searchKeydown(e)}
              onFocus={() => setShowSearchButtonPopover(true)}
              onBlur={() => setShowSearchButtonPopover(false)}
              onMouseEnter={() => setShowSearchButtonPopover(true)}
              onMouseLeave={() => setShowSearchButtonPopover(false)}
              type="button"
              aria-label="navigate to search results"
              tabIndex={0}
              ref={searchButtonRef}>
              <HomefieldIconSearch width="24" height="24" />
            </button>
            {showSearchButtonPopover && (
              <Popover
                className="popover-container"
                open={showSearchButtonPopover}
                anchorElement={searchButtonRef.current}
                transparentBackground>
                <div className="label-inverse-base hmf-body-m hmf-p-xs">Search</div>
              </Popover>
            )}
          </div>
        </div>
        {showResultsDiv &&
          searchData &&
          searchInput?.length > 2 &&
          viewPortBreakpoint !== 'mobile' && (
            <ErrorBoundaryWithLogger componentId="SearchBar-SAYTModalWrapper">
              <SAYTModalWrapper
                open={showResultsDiv}
                onCloseModal={() => setShowResultsDiv(false)}
                headerRef={headerRef}
                searchInputRef={inputRef}
                clearButtonRef={clearButtonRef}
                searchButtonRef={searchButtonRef}
                viewPortBreakpoint={viewPortBreakpoint}>
                {saytRedesignEnabled ? (
                  <ErrorBoundaryWithLogger componentId="SAYTModalWrapper-SAYTSuggestionsV2">
                    <SAYTSuggestionsV2
                      storeIdentifierName={storeIdentifierName}
                      searchData={searchData}
                      submitSearch={submitSearch}
                      searchTerm={searchInput}
                      thumbnailApi={thumbnailApi}
                    />
                  </ErrorBoundaryWithLogger>
                ) : (
                  <ErrorBoundaryWithLogger componentId="SAYTModalWrapper-SAYTSuggestions">
                    <SAYTSuggestions
                      storeIdentifierName={storeIdentifierName}
                      thumbnailApi={thumbnailApi}
                      searchData={searchData}
                      submitSearch={submitSearch}
                      searchTerm={searchInput}
                      updateSearchTermSAYT={updateSearchTermSAYT}
                      onCloseModal={() => setShowResultsDiv(false)}
                    />
                  </ErrorBoundaryWithLogger>
                )}
              </SAYTModalWrapper>
            </ErrorBoundaryWithLogger>
          )}
      </div>
    </div>
  );
};
export default SearchBar;
